import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Bed, Bath, Square, ArrowRight, CheckCircle2, TrendingUp, Building2, Sun, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MiamiPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties?market=miami`);
      setProperties(response.data);
    } catch (err) {
      logger.error("Failed to fetch properties:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1740484408205-7f23daabbfdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtaWFtaSUyMHdhdGVyZnJvbnQlMjBjb25kbyUyMHRvd2VycyUyMHN1bm55JTIwZGF5JTIwYmx1ZSUyMHNreXxlbnwwfHx8fDE3NzMwOTU5OTZ8MA&ixlib=rb-4.1.0&q=85"
            alt="Miami luxury condominiums"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A2E]/80 via-[#1A1A2E]/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <motion.div 
            className="max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full mb-6">Miami & South Florida</span>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white leading-[1.1] mb-6" data-testid="miami-title">
              Miami 1031 Exchange Replacement Properties
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-xl">
              Explore potential replacement properties in one of America's fastest growing real estate markets. Rental use, demand, and performance are subject to market conditions, local regulations, and property-specific factors. Buyers should conduct independent due diligence.
            </p>
            <Link to="/contact">
              <Button className="btn-primary px-8 py-6 text-base rounded-lg group font-semibold">
                Register for Miami Properties
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Market Stats */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "6.1M", label: "Metro Population" },
              { value: "#1", label: "Migration Destination" },
              { value: "0%", label: "State Income Tax" },
              { value: "23M", label: "Annual Visitors" },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <p className="text-4xl md:text-5xl font-bold text-[#1A1A2E] mb-2">{stat.value}</p>
                <p className="text-sm text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Miami */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline mb-4 block">Market Overview</span>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight mb-6">
                Why Consider Miami
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                South Florida has emerged as one of the most active real estate markets in the United States. Migration from high-tax states, expanding financial and technology sectors, and international interest have contributed to growth in the region.
              </p>

              <ul className="space-y-4">
                {[
                  "Rapid population growth and migration from high-tax states",
                  "Expanding financial, technology, and international business sectors",
                  "Global buyer demand from Latin America and Europe",
                  "Established luxury market with a broad rental base",
                  "No state income tax environment",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-6"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                { icon: TrendingUp, title: "Growth Market", desc: "One of the fastest appreciating markets in the US" },
                { icon: Globe, title: "Global Appeal", desc: "International buyer interest from around the world" },
                { icon: Sun, title: "Lifestyle", desc: "Year-round climate attracts premium tenants" },
                { icon: MapPin, title: "Prime Areas", desc: "Brickell, Miami Beach, Sunny Isles" },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 bg-white border border-slate-100"
                >
                  <item.icon className="w-8 h-8 text-[#0066FF] mb-4" strokeWidth={1.5} />
                  <h3 className="text-lg font-medium text-[#1A1A2E] mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Properties */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">Available Properties</span>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight">
              Miami Condo Unit Listings
            </h2>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="spinner w-8 h-8" />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-16 bg-gradient-to-br from-blue-50 to-slate-50 border border-slate-100 rounded-lg">
              <Building2 className="w-12 h-12 text-[#0066FF] mx-auto mb-4" />
              <h3 className="text-xl font-medium text-[#1A1A2E] mb-2">Miami Properties Available</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Registration is required to view available condo unit details, pricing, and property metrics.
              </p>
              <Link to="/contact">
                <Button className="btn-primary rounded-sm">Register to View Properties</Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.slice(0, 6).map((property, idx) => (
                <motion.div
                  key={property.property_id}
                  className="property-card group bg-white border border-slate-100 overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={property.images[0] || "https://images.unsplash.com/photo-1740484409661-75d41ff41a9b?w=800"}
                      alt={property.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {property.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-[#0066FF] text-white text-xs font-medium uppercase tracking-wider">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-medium text-[#1A1A2E] mb-1">{property.title}</h3>
                    <div className="flex items-center gap-1 text-slate-500 text-sm mb-4">
                      <MapPin className="w-4 h-4" />
                      <span>{property.location}</span>
                    </div>

                    <div className="flex items-center gap-4 mb-6 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        <span>{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Bath className="w-4 h-4" />
                        <span>{property.bathrooms}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Square className="w-4 h-4" />
                        <span>{property.sqft.toLocaleString()} SF</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-2xl font-bold text-[#1A1A2E]">
                        {formatPrice(property.price)}
                      </span>
                      <Link to={`/properties/${property.property_id}`}>
                        <Button variant="ghost" className="text-[#0066FF] hover:text-[#1D4ED8] px-0">
                          Details
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready to Explore Miami Properties?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Our team can help you identify Miami condo unit replacement properties that align with your 1031 exchange timeline and objectives.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#1A1A2E] hover:bg-slate-100 px-8 py-6 text-base rounded-sm">
                Schedule a Consultation
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
